import {
    Contract,
    ContractDetails as TContractDetails,
    MyProfileData,
    EarlyReturnPenaltyRequest,
    getEarlyReturnPenaltyEndpoint,
    frontEndEarlyReturnPenaltyValidationSchema,
} from 'cpfr-common';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonContainer, Fieldset, Layout, Notification } from '@vwfs-bronson/bronson-react';
import { NoConnectionNotification } from 'components/notifications/no-connection/NoConnectionNotification';
import { useHistory } from 'react-router-dom';
import { dashboardPagePath } from 'components/navigation/paths';
import { Form, Formik } from 'formik';
import { CleaveInput, ContractHeader, Spinner } from '@cp-shared-19/frontend-ui';
import { getInitialValues, EarlyReturnPenaltyFormValues } from './initialValues';
import { LicensePlate } from 'components/license-plate/LicensePlate';
import { CpDataApi } from 'cp-xhr';
import { AvailableDateSelector } from './AvailableDateSelector';
import { EarlyReturnPenaltyModal } from './EarlyReturnPenaltyModal';
import { EmailInputModal } from '../../request/form-view/email-input/EmailInputModal';
import { getErrorCode, isDuplicateSubmissionError, useSubmitOnce } from 'utils';
// import { useMyProfile } from 'components/my-profile';

type RequestProps = {
    status?: 'error' | 'success' | 'inProgress';
};

const USER_WITH_NO_EMAIL_CODE = 'EMAIL_NOT_FOUND_OR_PROVIDED';

export const EarlyReturnPenaltyUi: React.FC<{
    contract?: Contract & TContractDetails;
    myProfile?: MyProfileData;
}> = ({ contract, myProfile }) => {
    const { t } = useTranslation(['early-return-penalty', 'loader']);
    const history = useHistory();
    // const { data, isLoading } = useMyProfile();
    const [requestProgress, setRequestProgress] = useState<RequestProps>({});
    const { isSubmitting, run } = useSubmitOnce();
    const [modalCustomTextKey, setModalCustomTextKey] = useState<string | null>(null);
    const [showSuccessNotification, setShowSuccessNotification] = useState(false);

    if (!contract || !myProfile) return <NoConnectionNotification />;

    const {
        contractNumber,
        productTypeFR = '',
        carModel = '',
        licensePlate = '',
        contractEndDate,
        contractStartDate,
    } = contract;
    const customerNumber = myProfile.identification?.data.customerNumber;

    if (!contractNumber || !customerNumber || !contractEndDate || !contractStartDate)
        return <NoConnectionNotification />;

    const errorMessages = {
        requestDate: {
            required: t('form.validation.date-empty'),
            min: t('form.validation.date-in-future'),
            deliveryDatePlusNineMonths: t('form.validation.delivery-date-plus-nine-months'),
            afterContractEnds: t('form.validation.after-contract-ends'),
        },
        estimatedMileage: {
            required: t('form.validation.mileage-empty'),
            positive: t('form.validation.mileage-empty'),
        },
        email: {
            required: 'Veuillez renseigner votre adresse e-mail.',
            mustBeEmail: "Veuillez corriger l'adresse e-mail saisie.",
        },
    };

    const handleSubmit = (data: EarlyReturnPenaltyFormValues): Promise<void> =>
        run(async () => {
            setRequestProgress({ status: 'inProgress' });
            setModalCustomTextKey(null);
            const body: EarlyReturnPenaltyRequest = {
                ...data,
                estimatedMileage: Number(data.estimatedMileage),
                customerNumber: customerNumber,
                contractNumber: contractNumber,
                contractEndDate: contractEndDate,
                contractStartDate: contractStartDate,
            };

            try {
                await CpDataApi.post(getEarlyReturnPenaltyEndpoint(), body);
            } catch (error) {
                // A duplicate is not a failure: the request is already registered,
                // so fall through to the success path instead of offering a retry.
                if (!isDuplicateSubmissionError(error)) {
                    if (getErrorCode(error) === USER_WITH_NO_EMAIL_CODE) {
                        setModalCustomTextKey('missingEmail');
                    }
                    setRequestProgress({ status: 'error' });
                    return;
                }
            }

            setRequestProgress({ status: 'success' });
            setShowSuccessNotification(true);
        });

    return (
        <>
            {requestProgress.status === 'inProgress' && (
                <Spinner center>
                    <p>{t('genericText', { ns: 'loader' })}</p>
                </Spinner>
            )}
            <Layout>
                <Layout.Item default="1/1" s="1/1">
                    {showSuccessNotification && (
                        <Notification
                            title={t('success-notification.title')}
                            className="u-mb"
                            visible
                            testId="contract-success-notification"
                            showCloseButton={false}
                            status={'success'}
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: t('success-notification.text'),
                                }}
                            />
                        </Notification>
                    )}
                    <div role="group" className="c-card" aria-label={t('contract-details-aria-label')}>
                        <div className="c-card__body">
                            <ContractHeader
                                carInformation={carModel}
                                contractCategory={productTypeFR}
                                contractNumber={contractNumber}
                                contractIdentifier={<LicensePlate registrationNumber={licensePlate} />}
                            />
                        </div>
                    </div>
                </Layout.Item>
                <Layout.Item default="1/1">{t('info')}</Layout.Item>
                <Layout.Item default="1/1">
                    <Formik
                        initialValues={getInitialValues()}
                        onSubmit={(data) => handleSubmit(data)}
                        validationSchema={frontEndEarlyReturnPenaltyValidationSchema(
                            errorMessages,
                            modalCustomTextKey === 'missingEmail',
                        )}
                    >
                        {(formik) => (
                            <Form onSubmit={(e) => e.preventDefault()}>
                                <Fieldset>
                                    <Fieldset.Row>
                                        <Layout>
                                            <Layout.Item>
                                                <AvailableDateSelector contract={contract} />
                                            </Layout.Item>
                                            <Layout.Item>
                                                <CleaveInput
                                                    requiredText=""
                                                    className="c-form-field--no-floating"
                                                    aria-required={true}
                                                    cleaveOptions={{
                                                        numeral: true,
                                                        numeralDecimalMark: ',',
                                                        delimiter: '',
                                                        numeralThousandsGroupStyle: 'thousand',
                                                        numericOnly: true,
                                                        numeralDecimalScale: 2,
                                                    }}
                                                    inputMode={'numeric'}
                                                    label={t('form.estimated-mileage.label')}
                                                    name={'estimatedMileage'}
                                                    testId={'estimated-mileage'}
                                                    placeholder={t('form.estimated-mileage.placeholder')}
                                                />
                                            </Layout.Item>
                                        </Layout>
                                    </Fieldset.Row>
                                    <Fieldset.Row>
                                        <ButtonContainer className={'u-mt'} center>
                                            <Button
                                                secondary
                                                onClick={(): void => history.push(dashboardPagePath())}
                                                testId="backButton"
                                                disabled={isSubmitting}
                                            >
                                                {t('form.button.cancel')}
                                            </Button>
                                            <Button
                                                onClick={(): void => formik.handleSubmit()}
                                                testId="sendButton"
                                                disabled={isSubmitting || showSuccessNotification}
                                            >
                                                {t('form.button.send')}
                                            </Button>
                                        </ButtonContainer>
                                    </Fieldset.Row>
                                </Fieldset>
                                <EmailInputModal
                                    show={modalCustomTextKey === 'missingEmail'}
                                    onClose={() => setModalCustomTextKey(null)}
                                    onConfirm={(): void => formik.handleSubmit()}
                                    disabled={isSubmitting}
                                />
                            </Form>
                        )}
                    </Formik>
                </Layout.Item>
            </Layout>
            <EarlyReturnPenaltyModal
                status={requestProgress.status}
                onModalClose={() => setRequestProgress({})}
                textKey={modalCustomTextKey}
            />
        </>
    );
};
